import {Inject, Injectable} from '@angular/core';
import {Observable, of, ReplaySubject} from "rxjs";
import {CountedAndPaginated} from "../interface/response/counted-and-paginated";
import {DobryAgentBlogCategory, DobryAgentBlogTag} from "../interface/dobry-agent-blog-article";
import {HttpClient} from "@angular/common/http";
import {FilterService} from "./filter.service";
import {DOCUMENT} from "@angular/common";
import {first, map, switchMap, take, tap} from "rxjs/operators";
import {Uuid} from "../interface/response/uuid";
import {ListResponse} from "../interface/response/list-response";
import {ConfirmDialogComponent} from "../components/confirm-dialog/confirm-dialog.component";
import {MatSnackBar} from "@angular/material/snack-bar";
import {MatDialog} from "@angular/material/dialog";

@Injectable({
  providedIn: 'root'
})
export class DobryAgentBlogCategoryService {
  public readonly blogCategoryListSubject: ReplaySubject<CountedAndPaginated<DobryAgentBlogCategory>>
    = new ReplaySubject<CountedAndPaginated<DobryAgentBlogCategory>>(1);
  public readonly blogCategoryNotCountedListSubject: ReplaySubject<ListResponse<DobryAgentBlogCategory>> = new ReplaySubject<ListResponse<DobryAgentBlogCategory>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
    private snack: MatSnackBar,
    private dialog: MatDialog,
  ) {
  }

  public loadList(page: number, pageSize: number, filters: object, sort?: { sortBy?: string; sortOrder?: string}): void {
    this.httpClient.get(`/rest/dobryagent/api/internal/blog-category?page=${page + 1}&pageSize=${pageSize}&sortBy=${sort.sortBy ?? ''}&sortOrder=${sort.sortOrder ?? ''}&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: CountedAndPaginated<DobryAgentBlogCategory>) => {
        this.blogCategoryListSubject.next(result);
      });
  }

  public loadNotCountedList(filters: object): void {
    this.httpClient
      .get(`/rest/dobryagent/api/internal/blog-category?disableCount=1&` + this.filterService.parseFilters(filters))
      .pipe(take(1))
      .subscribe((result: ListResponse<DobryAgentBlogCategory>) => {
        this.blogCategoryNotCountedListSubject.next(result);
      });
  }

  public getBlogCategoryNotCountedListSubject(): ReplaySubject<ListResponse<DobryAgentBlogCategory>> {
    return this.blogCategoryNotCountedListSubject;
  }

  public getBlogCategoryListSubject(): ReplaySubject<CountedAndPaginated<DobryAgentBlogCategory>> {
    return this.blogCategoryListSubject;
  }
  public get(uuid: string): Observable<DobryAgentBlogCategory> {
    return this.httpClient
      .get<DobryAgentBlogCategory>(`/rest/dobryagent/api/internal/blog-category/${uuid}`)
      .pipe(take(1));
  }

  public send(formData: object, uuid?: string): Observable<Uuid> {
    if (uuid) {
      return this.httpClient
        .put<Uuid>(`/rest/dobryagent/api/internal/blog-category/${uuid}`, formData)
        .pipe(take(1));
    }

    return this.httpClient
      .post<Uuid>(`/rest/dobryagent/api/internal/blog-category`, formData)
      .pipe(take(1));
  }

  public uploadIcon(file: File, blogCategoryUuid: string): Observable<any> {
    const formData = new FormData();
    formData.append('content', file);

    return this.httpClient
      .post(
        `/rest/dobryagent/api/internal/blog-category/${blogCategoryUuid}/icon-upload`,
        formData
      )
      .pipe(take(1));
  }

  public deleteIcon(blogCategoryUuid: string): Observable<void> {
    return this.httpClient
      .delete<void>(
        `/rest/dobryagent/api/internal/blog-category/${blogCategoryUuid}/icon-delete`
      )
      .pipe(take(1));
  }

  private delete(uuid: string): Observable<void> {
    return this.httpClient
      .delete<void>(
        `/rest/dobryagent/api/internal/blog-category/${uuid}/delete`
      )
      .pipe(first());
  }

  private deleteWeak(uuid: string): Observable<void> {
    return this.httpClient
      .patch<void>(
        `/rest/dobryagent/api/internal/blog-category/${uuid}`,
        {}
      )
      .pipe(first());
  }


  private restore(uuid: string): Observable<void> {
    return this.httpClient
      .patch<void>(
        `/rest/dobryagent/api/internal/blog-category/${uuid}/restore`,
        {}
      )
      .pipe(first());
  }

  public confirmDelete(
    blogTag: DobryAgentBlogTag
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz usunąć kategorię?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.delete(blogTag.uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie usunięto kategorię',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }

  public confirmDeleteWeak(
    uuid: string
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz przenieść kategorię do archiwum?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.deleteWeak(uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie przeniesiono kategorię do archiwum',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }


  public confirmRestore(
    blogTag: DobryAgentBlogTag
  ): Observable<boolean> {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '500px',
      data: {
        text: 'Czy na pewno chcesz przywrócić kategorię z archiwum?'
      },
    });

    return dialogRef.afterClosed()
      .pipe(
        take(1),
        switchMap((confirmed: boolean) => {
          if (!confirmed) {
            return of(false);
          }

          return this.restore(blogTag.uuid)
            .pipe(
              tap(() => {
                this.snack.open(
                  'Pomyślnie przywrócono kategorię',
                  '',
                  {duration: 2000}
                );
              }),
              map(() => true)
            );
        })
      );
  }

  public updateArticleOrder(
    blogCategoryUuid: string,
    articleUuids: string[]
  ): Observable<void> {
    return this.httpClient
      .put<void>(
        `/rest/dobryagent/api/internal/blog-category/${blogCategoryUuid}/article-order`,
        {
          articles: articleUuids
        }
      )
      .pipe(take(1));
  }
}
